import { Children, useCallback, useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import { IconButton } from '@/components/icon-button/IconButton'
import { ChevronLeft, ChevronRight } from '@/assets/icons'

// The element roving focuses within each item: its root button/link (or another focusable).
const kItemFocusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]'

export type CarouselProps = {
  /** Items rendered in the horizontal track. */
  children: ReactNode
  /** Accessible name for the carousel. */
  ariaLabel?: string
  /** Optional class for the outer wrapper. */
  className?: string
  /** Shown inside the container when there are no children — an empty, error, or other "no items" state. A plain string, or a node. */
  noItemsSlot?: ReactNode
  /** Min height (px) of the no-items slot, so it doesn't collapse. Override to match taller items. */
  noItemsMinHeight?: number
  /** When true, the track doesn't scroll and the arrows show as disabled. For non-navigable content like a loading placeholder. */
  disableInteraction?: boolean
}

/**
 * Carousel
 *
 * A horizontal, scrollable track of items. Scroll natively (trackpad swipe, touch,
 * or Shift + mouse wheel) or with the side buttons, which disable at each end.
 *
 * Layout note: the prev/next buttons sit just *outside* the track (standard carousel approach, e.g.
 * Embla, Swiper) so it stays full-width and aligned with sibling content. The caller owns the gutter:
 * mount this in a container with ≥~40px horizontal padding or they clip. It's deliberately not
 * self-carved (negative margins would couple it to the parent's padding and clip under overflow-hidden).
 */
export function Carousel({
  children,
  ariaLabel = 'Carousel',
  className,
  noItemsSlot = 'No items to display.',
  noItemsMinHeight = 60,
  disableInteraction = false,
}: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateArrows = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(Math.ceil(el.scrollLeft) < el.scrollWidth - el.clientWidth - 1)
  }, [])

  // Set up the resize observer once.
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    const ro = new ResizeObserver(updateArrows)
    ro.observe(el)
    return () => ro.disconnect()
  }, [updateArrows])

  // Recompute the arrow state when the items change — the observer only watches the track's size, not its content.
  useEffect(() => {
    updateArrows()
  }, [updateArrows, children])

  const scrollByPage = (direction: 1 | -1) => {
    const el = trackRef.current
    if (!el) return
    // a11y: respect the user's reduced-motion preference and skip the smooth-scroll animation.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: prefersReduced ? 'auto' : 'smooth' })
  }

  const isEmpty = Children.toArray(children).length === 0
  // Roving is off for the no-items slot (its own content owns its tab order) and whenever interaction
  // is disabled (the items aren't navigable, same as the scroll arrows).
  const rovingEnabled = !isEmpty && !disableInteraction

  // Each item's focusable element (its root button/link), read from the DOM — roving sets tabindex and
  // moves focus on these nodes, which live inside arbitrary children. Items without a focusable are skipped.
  const getItemFocusables = useCallback((): HTMLElement[] => {
    const track = trackRef.current
    if (!track) return []

    return Array.from(track.children)
      .map((wrapper) => wrapper.querySelector<HTMLElement>(kItemFocusableSelector))
      .filter((el): el is HTMLElement => el != null)
  }, [])

  // Make `active` the lone tab stop (tabindex 0) and drop every other item from the tab order (-1).
  const setTabStop = useCallback(
    (active: HTMLElement) => {
      for (const el of getItemFocusables()) el.tabIndex = el === active ? 0 : -1
    },
    [getItemFocusables]
  )

  // Ensure one tab stop exists on mount and when items change — keep the current one, else the first.
  useEffect(() => {
    if (!rovingEnabled) return
    const items = getItemFocusables()
    if (items.length) setTabStop(items.find((el) => el.tabIndex === 0) ?? items[0])
  }, [getItemFocusables, setTabStop, children, rovingEnabled])

  const handleTrackKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!rovingEnabled) return
    const items = getItemFocusables()

    // Step from the item that currently holds focus, so consecutive presses keep moving across the track.
    const from = items.indexOf(document.activeElement as HTMLElement)
    if (from < 0) return

    const to = { ArrowLeft: from - 1, ArrowRight: from + 1, Home: 0, End: items.length - 1 }[e.key]
    if (to === undefined) return
    e.preventDefault() // don't also let the arrow key scroll the track

    // Focus without its own scroll, then scroll the item in ourselves so the track's scroll-padding applies.
    const target = items[Math.max(0, Math.min(to, items.length - 1))]
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    target.focus({ preventScroll: true })
    target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: prefersReduced ? 'auto' : 'smooth' })
  }

  // A click or Tab landing on an item makes it the tab stop, so Tab later returns to the last-focused item.
  const handleTrackFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (!rovingEnabled) return
    const el = e.target as HTMLElement
    if (getItemFocusables().includes(el)) setTabStop(el)
  }

  // When the track can't scroll at all, hide the buttons entirely.
  // Otherwise, show both and disable the one for the direction you've run out of.
  const isScrollable = canScrollLeft || canScrollRight

  return (
    <div data-ds-component="Carousel" role="group" aria-label={ariaLabel} className={clsx('relative', className)}>
      <div
        ref={trackRef}
        data-testid="carousel-track"
        onScroll={updateArrows}
        onKeyDown={handleTrackKeyDown}
        onFocus={handleTrackFocus}
        className={clsx(
          // scroll-px-4 matches the p-4 gutter so arrowing to the first/last item scrolls to the true start/end of the track.
          'flex min-w-0 gap-4 rounded-lg border border-brand-neutral-200 p-4 scroll-px-4',
          // overscroll-x-none keeps a horizontal overscroll from chaining out to ancestor scrollers, which would bounce and reveal the background behind the track.
          disableInteraction ? 'overflow-x-hidden' : 'overflow-x-auto overscroll-x-none',
          isEmpty ? 'bg-brand-neutral-100' : 'bg-brand-neutral-50',
          '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
        )}
      >
        {isEmpty ? (
          <div
            style={{ minHeight: noItemsMinHeight }}
            className="flex w-full items-center justify-center px-2 text-center text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-600"
          >
            {noItemsSlot}
          </div>
        ) : (
          // Each item is wrapped as a non-shrinking flex item so the track overflows (and scrolls)
          // instead of compressing items to fit. Items keep their own intrinsic width.
          Children.map(children, (child) => <div className="shrink-0">{child}</div>)
        )}
      </div>
      {isScrollable && (
        <>
          {/* right-full positions outside (to the left of) the track's left border */}
          <div className="absolute inset-y-0 right-full flex items-center pr-1">
            <IconButton
              variant="plain"
              color="black"
              size="md"
              icon={<ChevronLeft />}
              aria-label="Scroll left"
              onClick={() => scrollByPage(-1)}
              disabled={disableInteraction || !canScrollLeft}
              tabIndex={-1} // the arrows are a pointer affordance, kept out of the tab order (see Keyboard note)
              // hitting the scroll end is a benign boundary, not a forbidden action, so keep a plain cursor
              className="disabled:!cursor-default"
            />
          </div>
          {/* left-full positions outside (to the right of) the track's right border */}
          <div className="absolute inset-y-0 left-full flex items-center pl-1">
            <IconButton
              variant="plain"
              color="black"
              size="md"
              icon={<ChevronRight />}
              aria-label="Scroll right"
              onClick={() => scrollByPage(1)}
              disabled={disableInteraction || !canScrollRight}
              tabIndex={-1}
              className="disabled:!cursor-default"
            />
          </div>
        </>
      )}
    </div>
  )
}
